import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { kpiMatrix, suppliers as suppliersApi } from "@/api";
import { RubricPanel } from "@/components/RubricPanel";
import PlantAverageReal from "@/components/PlantAverageReal";
import { formatLevel, levelGap, plantRealView } from "@/lib/plantReal";
import { ArrowLeft, Grid3x3, Save, Target, Activity, ChevronDown, ChevronRight } from "lucide-react";

const LEVEL_COLORS = {
  1: "#E0162B", 2: "#F59E0B", 3: "#3B82F6", 4: "#10B981",
};

export default function KpiMatrix() {
  const [suppliers, setSuppliers] = useState([]);
  const [sid, setSid] = useState("");
  const [period, setPeriod] = useState(`${new Date().getFullYear()}`);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savedCells, setSavedCells] = useState({});
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    suppliersApi.list().then((res) => {
      const arr = res.items || res;
      setSuppliers(arr);
      if (arr.length > 0 && !sid) setSid(arr[0].id);
    }).catch((e) => setErr(e.response?.data?.detail || e.message));
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    if (!sid) return;
    setBusy(true); setErr("");
    try {
      const res = await kpiMatrix.get(sid, period);
      setData(res);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sid, period]);

  const grouped = useMemo(() => {
    if (!data?.indicators) return [];
    const byCrit = {};
    for (const ind of data.indicators) {
      const k = `${ind.pillar}::${ind.criterion_code}`;
      byCrit[k] = byCrit[k] || { pillar: ind.pillar, code: ind.criterion_code, label: ind.criterion_label, indicators: [] };
      byCrit[k].indicators.push(ind);
    }
    return Object.values(byCrit);
  }, [data]);

  const saveCell = async (indicator_code, fields) => {
    setBusy(true); setErr("");
    try {
      const payload = {
        indicator_code,
        period,
        objetivo_level: fields.objetivo_level ?? null,
        // Sin "real_level" en fields no se envía: en Nivel de Servicio con varias plantas el Real es
        // el promedio de las plantas y no se escribe desde aquí.
        ...("real_level" in fields ? { real_level: fields.real_level ?? null } : {}),
        notes: fields.notes || "",
      };
      await kpiMatrix.upsert(sid, payload);
      setSavedCells((p) => ({ ...p, [indicator_code]: true }));
      setTimeout(() => setSavedCells((p) => ({ ...p, [indicator_code]: false })), 1500);
      await load();
      setMsg("Captura guardada.");
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="kpi-matrix-page">
      <Link to="/admin/catalogo-kpis" className="text-sm text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Volver al catálogo
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
            <Grid3x3 size={18} /> matriz operativa
          </div>
          <h1 className="font-display text-4xl">Matriz de KPIs por proveedor</h1>
          <p className="text-[var(--ink-soft)] mt-1 max-w-3xl">
            Captura granular del nivel <strong>Objetivo</strong> y <strong>Real</strong> (1-4) por indicador.
            Es la matriz que Lala mantiene viva mes a mes para acompañar el crecimiento de cada proveedor.
            El score de pilar se deriva automáticamente del promedio de niveles.
          </p>
        </div>
      </div>

      {/* Selector */}
      <div className="sketch-border bg-[var(--cream-soft)] p-4 mb-6 flex flex-wrap items-end gap-3">
        <label className="block">
          <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">Proveedor</div>
          <select value={sid} onChange={(e) => setSid(e.target.value)} className="input-field min-w-[240px]" data-testid="matrix-supplier-select">
            {suppliers.map((s) => <option key={s.id} value={s.id} label={`${s.name} · ${s.category}`} />)}
          </select>
        </label>
        <label className="block">
          <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">Periodo</div>
          <input type="text" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026, 2026-Q1, etc." className="input-field" data-testid="matrix-period-input" />
        </label>
        <button onClick={load} disabled={busy} className="btn-primary text-sm">Cargar</button>
      </div>

      {/* Pillar summary */}
      {data?.pillars?.length > 0 && (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mb-6" data-testid="matrix-pillar-summary">
          {data.pillars.map((p) => (
            <div key={p.pillar} className="sketch-border bg-white p-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--lala-blue)]">Pilar · {p.pillar_label}</div>
              <div className="flex items-end gap-3 mt-1">
                <div>
                  <div className="font-display text-3xl text-[var(--ink)]" data-testid={`derived-real-${p.pillar}`}>
                    {p.derived_real_score != null ? p.derived_real_score : "—"}
                  </div>
                  <div className="text-[10px] text-[var(--ink-soft)]">Real derivado · {p.indicators_with_real} caps</div>
                </div>
                <div>
                  <div className="font-display text-2xl text-[#FFC400]" data-testid={`derived-target-${p.pillar}`}>
                    {p.derived_objetivo_score != null ? p.derived_objetivo_score : "—"}
                  </div>
                  <div className="text-[10px] text-[var(--ink-soft)]">Meta derivada</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {msg && <div className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">{msg}</div>}
      {err && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}

      {/* Matrix grouped by criterion */}
      <div className="space-y-3">
        {grouped.map((g) => {
          const k = `${g.pillar}::${g.code}`;
          const open = expanded[k] ?? true;
          return (
            <div key={k} className="sketch-border bg-white overflow-hidden" data-testid={`matrix-criterion-${g.code}`}>
              <button onClick={() => setExpanded((p) => ({ ...p, [k]: !open }))} className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-[var(--cream-soft)] transition-colors">
                <div className="flex items-center gap-2 text-left">
                  {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--lala-blue)] font-bold">{g.code}</div>
                    <div className="font-display text-lg">{g.label}</div>
                  </div>
                </div>
                <div className="text-xs text-[var(--ink-soft)]">{g.indicators.length} ind</div>
              </button>
              {open && (
                <div className="divide-y divide-[var(--line)] border-t border-[var(--line)]">
                  {g.indicators.map((ind) => (
                    <MatrixRow key={ind.indicator_code} ind={ind} onSave={saveCell} saved={savedCells[ind.indicator_code]} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MatrixRow({ ind, onSave, saved }) {
  const cap = ind.capture || {};
  const [obj, setObj] = useState(cap.objetivo_level ?? "");
  const [real, setReal] = useState(cap.real_level ?? "");
  const [notes, setNotes] = useState(cap.notes ?? "");
  const [dirty, setDirty] = useState(false);

  const realView = plantRealView(cap, "");
  const realIsAverage = realView.kind === "average";

  const onPersist = () => {
    onSave(ind.indicator_code, {
      objetivo_level: obj === "" ? null : Number(obj),
      ...(realIsAverage ? {} : { real_level: real === "" ? null : Number(real) }),
      notes,
    });
    setDirty(false);
  };

  const shownReal = realIsAverage ? realView.value : real;
  const gap = levelGap(shownReal, obj);
  return (
    <div className="px-4 py-3" data-testid={`matrix-row-${ind.indicator_code}`}>
      <div className="grid md:grid-cols-[1fr,auto,auto,auto] gap-3 items-start">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">{ind.indicator_code} · {ind.subcriterion || ""}</div>
          <div className="font-display text-base">{ind.indicator_label}</div>
          {ind.evaluation_instrument && (
            <div className="text-[10px] text-[var(--ink-soft)] mt-0.5"><Activity size={9} className="inline mr-1" />{ind.evaluation_instrument.slice(0, 100)}</div>
          )}
        </div>
        <LevelSelector value={obj} onChange={(v) => { setObj(v); setDirty(true); }} label="Objetivo" icon={<Target size={11} />} testid={`obj-${ind.indicator_code}`} />
        {realIsAverage ? (
          <PlantAverageReal label="Real" icon={<Activity size={11} />} value={realView.value}
            plantCount={realView.count} hint="Se captura por planta en Captura mensual."
            testid={`real-${ind.indicator_code}`} />
        ) : (
          <LevelSelector value={real} onChange={(v) => { setReal(v); setDirty(true); }} label="Real" icon={<Activity size={11} />} testid={`real-${ind.indicator_code}`} />
        )}
        <div className="flex flex-col items-end gap-1 min-w-[100px]">
          {shownReal !== "" && obj !== "" && (
            <div className={`text-xs font-bold ${gap >= 0 ? "text-emerald-700" : "text-amber-700"}`}>
              gap: {gap > 0 ? "+" : ""}{gap}
            </div>
          )}
          {dirty && (
            <button onClick={onPersist} className="btn-primary text-xs justify-center" data-testid={`save-${ind.indicator_code}`}>
              <Save size={11} /> Guardar
            </button>
          )}
          {saved && (
            <div className="text-[10px] text-emerald-700">✓ Guardado</div>
          )}
        </div>
      </div>
      <input
        type="text" value={notes} onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
        placeholder="Comentario interno (opcional)"
        className="input-field mt-2 text-xs"
        data-testid={`notes-${ind.indicator_code}`}
      />
      <RubricPanel levels={ind.levels || []} selected={realIsAverage ? "" : real}
        onPick={realIsAverage ? undefined : (n) => { setReal(n); setDirty(true); }}
        testid={`matrix-rubric-${ind.indicator_code}`} defaultOpen={false} />
      {ind.capture?.plants?.length > 0 && (
        <div className="mt-2 rounded-lg border border-[var(--line)] bg-[var(--cream-soft)] p-2" data-testid={`plant-breakdown-${ind.indicator_code}`}>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--lala-blue)] mb-1">
            Promedio simple de {ind.capture.plants.length} planta(s) → Real {formatLevel(ind.capture.real_level)}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ind.capture.plants.map((p) => (
              <span key={p.plant} className="text-[11px] px-2 py-0.5 rounded-md bg-white border border-[var(--line)]"
                data-testid={`plant-score-${ind.indicator_code}-${p.plant}`}>
                <b>{p.plant}</b>: {p.real_level ?? "—"}
              </span>
            ))}
          </div>
        </div>
      )}
      {cap.updated_at && (
        <div className="text-[10px] text-[var(--ink-soft)] mt-1">
          Última captura: {new Date(cap.updated_at).toLocaleString("es-MX")} {cap.updated_by ? `· ${cap.updated_by}` : ""}
        </div>
      )}
    </div>
  );
}

function LevelSelector({ value, onChange, label, icon, testid }) {
  return (
    <div className="flex flex-col items-start gap-1">
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold flex items-center gap-1">{icon} {label}</div>
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(value === n ? "" : n)}
            data-testid={`${testid}-${n}`}
            className={`w-8 h-8 rounded-full text-sm font-bold transition-all ${value === n ? "text-white scale-110 shadow-md" : "bg-[var(--cream-deep)] text-[var(--ink-soft)] hover:bg-[var(--cream)]"}`}
            style={value === n ? { background: LEVEL_COLORS[n] } : {}}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}
